import React, { useEffect, useRef, useState } from 'react';
import showdown from 'showdown';
import useStyles from 'isomorphic-style-loader/useStyles';
import classNames from 'classnames';
import hljs from 'highlight.js';
import s from './AIChatPlaygroundTextMessagePart.scss';
import { ConversationRole, TextMessagePart } from '../../../aIChatPlaygroundInterface';
import { AuthService, Container } from '../../../../../symphony';
import { IconAvatarFill } from '../../../../../shared/public/assets/icons/IconAvatarFill';
import { CopyIcon } from '../../../../../shared/public/assets/icons/CopyIcon';
import { ClickType, aIChatPlaygroundContext } from '../../../aIChatPlaygroundConstants';
import { AIChatPlaygrountTrackingService } from '../../../services/AIChatPlaygrountTrackingService';
import { AIRegenerateIcon } from '../../../../../shared/public/assets/icons/AIRegenerateIcon';
import { useContainerData } from '../../../../../shared/public/SlHooks/useContainerData';
import { AIChatPlaygroundStore } from '../../../AIChatPlaygroundStore';
import { AIChatPlaygroundService } from '../../../services/AIChatPlaygroundService';

interface IAIChatPlaygroundTextMessagePartProps {
  content: TextMessagePart['content'],
  role: ConversationRole,
  index: number;
}

export const AIChatPlaygroundTextMessagePart = ({ content, role, index }: IAIChatPlaygroundTextMessagePartProps) => {
  useStyles(s);
  const trackingService = Container.take(aIChatPlaygroundContext.value, AIChatPlaygrountTrackingService);
  const { regenerateMessage } = Container.take(aIChatPlaygroundContext.value, AIChatPlaygroundService);
  const [message, setMessage] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const { userInfo$ } = Container.take('global', AuthService);
  const messageRef = useRef<HTMLDivElement>(null);
  const { conversation, isResponseLoading } = useContainerData(
    aIChatPlaygroundContext.value,
    AIChatPlaygroundStore,
    ['conversation', 'isResponseLoading'],
  );

  const copyHandler = () => {
    trackingService.trackPlaygroundClick(ClickType.CopyPrompt);
    navigator.clipboard.writeText(messageRef.current.textContent);
    setIsCopied(true);

    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  };

  useEffect(() => {
    if (role === ConversationRole.User) return;

    const converter = new showdown.Converter({ simpleLineBreaks: true, tables: true });
    const html = converter.makeHtml(content.text);
    setMessage(html);
  }, [content.text]);

  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.querySelectorAll('pre code').forEach((block: HTMLElement) => {
        hljs.highlightBlock(block);
      });
    }
  }, [message]);

  return (
    <div className={classNames('ai-chat-playground-text-message-part', role === ConversationRole.Assistant && 'assistant')}>
      {role === ConversationRole.User && (
        <div className="ai-chat-playground-text-message-part__avatar">
          {userInfo$.value.avatarUrl ? <img src={userInfo$.value.avatarUrl} alt="avatar" /> : <IconAvatarFill />}
        </div>
      )}
      {role === ConversationRole.User ? (
        <div
          ref={messageRef}
          className="ai-chat-playground-text-message-part__text"
        >
          {content.text}
        </div>
      ) : (
        <div
          ref={messageRef}
          className="ai-chat-playground-text-message-part__text"
          dangerouslySetInnerHTML={{ __html: message }}
        />
      )}
      <div className="ai-chat-playground-text-message-part__actions">
        <div className="ai-chat-playground-text-message-part__copy">
          <div className={classNames('ai-chat-playground-text-message-part__copied', isCopied && 'show')}>
            <CopyIcon
              className="ai-chat-playground-text-message-part__copied-icon"
            />
            Copied
          </div>
          <div
            onClick={copyHandler}
            className="ai-chat-playground-text-message-part__copy-icon-container"
          >
            <CopyIcon
              className="ai-chat-playground-text-message-part__copy-icon"
            />
          </div>
        </div>
        {
          conversation.length === index + 1 && !isResponseLoading && role === ConversationRole.Assistant && (
            <div
              onClick={regenerateMessage}
              className="ai-chat-playground-text-message-part__regenerate"
            >
              <AIRegenerateIcon />
            </div>
          )
        }
      </div>
    </div>
  );
};
